<script setup lang="ts">
/**
 * QuickUploadSheet Component
 * Quick receipt upload with multi-campaign selection
 * - Photo capture/upload area
 * - Multi-select campaign checkboxes
 * - Potential earnings display
 * - Submit creates tickets for selected campaigns
 */

import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useChallengesStore, useTicketsStore } from '@/stores'
import { BottomSheet } from '@/components/mobile'
import { formatCurrency } from '@/utils/currency'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import ProgressBar from 'primevue/progressbar'

const { t } = useI18n()

interface Props {
  visible: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  success: [count: number]
}>()

const challengesStore = useChallengesStore()
const ticketsStore = useTicketsStore()

// Local state
const selectedFile = ref<File | null>(null)
const imagePreview = ref<string | null>(null)
const selectedChallengeIds = ref<string[]>([])
const error = ref<string | null>(null)
const errorMessageRef = ref<HTMLElement | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)

// Computed
const activeChallenges = computed(() => challengesStore.activeChallenges)
const isSubmitting = computed(() => ticketsStore.isUploading)
const uploadProgress = computed(() => ticketsStore.uploadProgress?.progress || 0)
const isProcessing = computed(() => ticketsStore.uploadProgress?.status === 'processing')

const selectedChallenges = computed(() =>
  activeChallenges.value.filter(c => selectedChallengeIds.value.includes(c.id))
)

const potentialEarnings = computed(() =>
  selectedChallenges.value.reduce((sum, c) => sum + c.rewardAmount, 0)
)

const canSubmit = computed(() =>
  selectedFile.value !== null &&
  selectedChallengeIds.value.length > 0 &&
  !ticketsStore.isUploading
)

// Load active challenges when sheet opens
watch(() => props.visible, async (isVisible) => {
  if (isVisible && activeChallenges.value.length === 0) {
    await challengesStore.fetchChallenges()
  }
  if (!isVisible) {
    resetForm()
  }
})

onMounted(async () => {
  if (activeChallenges.value.length === 0) {
    await challengesStore.fetchChallenges()
  }
})

// File handling
function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    processFile(file)
  }
}

function processFile(file: File) {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    error.value = t('quickUpload.invalidImage')
    return
  }

  // Validate file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    error.value = t('quickUpload.imageTooLarge')
    return
  }

  selectedFile.value = file
  error.value = null

  // Create preview
  const reader = new FileReader()
  reader.onload = (e) => {
    imagePreview.value = e.target?.result as string
  }
  reader.readAsDataURL(file)
}

function openCamera() {
  fileInputRef.value?.click()
}

function removeImage() {
  selectedFile.value = null
  imagePreview.value = null
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

// Challenge selection
function toggleChallenge(challengeId: string) {
  const index = selectedChallengeIds.value.indexOf(challengeId)
  if (index === -1) {
    selectedChallengeIds.value.push(challengeId)
  } else {
    selectedChallengeIds.value.splice(index, 1)
  }
}

function isSelected(challengeId: string): boolean {
  return selectedChallengeIds.value.includes(challengeId)
}

// Submit
async function handleSubmit() {
  if (!canSubmit.value || !selectedFile.value) return

  error.value = null

  try {
    const result = await ticketsStore.uploadTicketBatch(
      selectedChallengeIds.value,
      selectedFile.value
    )

    if (result.successCount === 0) {
      throw new Error(t('quickUpload.noTicketCreated'))
    }

    emit('success', result.successCount)
    handleClose()
  } catch (e) {
    error.value = e instanceof Error ? e.message : t('quickUpload.uploadError')
    await nextTick()
    errorMessageRef.value?.focus()
    if (typeof errorMessageRef.value?.scrollIntoView === 'function') {
      errorMessageRef.value.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }
}

function handleClose() {
  emit('update:visible', false)
}

function resetForm() {
  selectedFile.value = null
  imagePreview.value = null
  selectedChallengeIds.value = []
  error.value = null
  ticketsStore.clearUploadProgress()
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}
</script>

<template>
  <BottomSheet
    :visible="visible"
    :title="t('quickUpload.title')"
    height="full"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="quick-upload-content">
      <!-- Photo upload area -->
      <div class="photo-section">
        <input
          ref="fileInputRef"
          type="file"
          accept="image/*"
          capture="environment"
          class="hidden-input"
          @change="handleFileSelect"
        />

        <!-- No image selected -->
        <button
          v-if="!imagePreview"
          type="button"
          class="photo-upload-btn"
          @click="openCamera"
        >
          <div class="photo-upload-icon">
            <i class="pi pi-camera" aria-hidden="true"></i>
          </div>
          <span class="photo-upload-text">{{ t('quickUpload.takePhoto') }}</span>
          <span class="photo-upload-hint">{{ t('quickUpload.fromGallery') }}</span>
        </button>

        <!-- Image preview -->
        <div v-else class="photo-preview">
          <img :src="imagePreview" :alt="t('quickUpload.previewAlt')" />
          <button
            type="button"
            class="photo-remove-btn"
            :aria-label="t('quickUpload.removeImage')"
            @click="removeImage"
          >
            <i class="pi pi-times" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <!-- Campaign selection -->
      <div class="campaigns-section">
        <div class="section-header">
          <h3 class="section-title">{{ t('quickUpload.selectChallenges') }}</h3>
          <span class="section-count">
            {{ t('quickUpload.selectedCount', { count: selectedChallengeIds.length }) }}
          </span>
        </div>

        <div v-if="activeChallenges.length === 0" class="empty-campaigns">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          <p>{{ t('quickUpload.noActiveChallenges') }}</p>
        </div>

        <div v-else class="campaigns-list">
          <button
            v-for="challenge in activeChallenges"
            :key="challenge.id"
            type="button"
            class="campaign-item"
            :class="{ 'campaign-selected': isSelected(challenge.id) }"
            @click="toggleChallenge(challenge.id)"
          >
            <div class="campaign-checkbox">
              <Checkbox
                :modelValue="isSelected(challenge.id)"
                :binary="true"
                :inputId="`campaign-${challenge.id}`"
                @click.stop
                @update:modelValue="toggleChallenge(challenge.id)"
              />
            </div>
            <img
              :src="challenge.imageUrl"
              :alt="challenge.brand.name"
              class="campaign-image"
            />
            <div class="campaign-info">
              <span class="campaign-brand">{{ challenge.brand.name }}</span>
              <span class="campaign-title">{{ challenge.title }}</span>
            </div>
            <span class="campaign-reward">
              {{ formatCurrency(challenge.rewardAmount) }}
            </span>
          </button>
        </div>
      </div>

      <!-- Error message -->
      <div
        v-if="error"
        ref="errorMessageRef"
        class="error-message"
        role="alert"
        tabindex="-1"
      >
        <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
        <span>{{ error }}</span>
      </div>

      <!-- Progress bar during upload -->
      <div v-if="isSubmitting" class="upload-progress">
        <ProgressBar :value="uploadProgress" :showValue="false" />
        <span class="progress-text">
          <template v-if="isProcessing">{{ t('quickUpload.processing') }}</template>
          <template v-else>{{ t('quickUpload.uploading', { progress: uploadProgress }) }}</template>
        </span>
      </div>
    </div>

    <template #footer>
      <div class="footer-content">
        <!-- Earnings summary -->
        <div v-if="selectedChallengeIds.length > 0" class="earnings-summary">
          <span class="earnings-label">{{ t('quickUpload.potentialEarnings') }}</span>
          <span class="earnings-amount">{{ formatCurrency(potentialEarnings) }}</span>
        </div>

        <!-- Disclaimer -->
        <div class="upload-disclaimer">
          <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
          <span>{{ t('quickUpload.disclaimer') }}</span>
        </div>

        <!-- Submit button -->
        <Button
          :label="isSubmitting ? t('quickUpload.submitUploading') : t('quickUpload.submit', { count: selectedChallengeIds.length }, selectedChallengeIds.length)"
          :icon="isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"
          :disabled="!canSubmit"
          class="submit-btn"
          @click="handleSubmit"
        />
      </div>
    </template>
  </BottomSheet>
</template>

<style scoped>
.quick-upload-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Photo section */
.photo-section {
  width: 100%;
}

.hidden-input {
  display: none;
}

.photo-upload-btn {
  width: 100%;
  padding: 2rem 1rem;
  border: 2px dashed var(--neutral-300);
  border-radius: 16px;
  background: var(--neutral-50);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  transition: all var(--duration-fast) ease;
  -webkit-tap-highlight-color: transparent;
}

.photo-upload-btn:hover,
.photo-upload-btn:focus {
  border-color: var(--color-pg-blue);
  background: rgba(0, 61, 165, 0.04);
}

.photo-upload-btn:active {
  transform: scale(0.98);
}

.photo-upload-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-pg-blue) 0%, var(--color-pg-blue-light) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
}

.photo-upload-text {
  font-family: var(--font-sans);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--neutral-800);
}

.photo-upload-hint {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--neutral-500);
}

/* Photo preview */
.photo-preview {
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: var(--neutral-100);
}

.photo-preview img {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: cover;
  display: block;
}

.photo-remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: background var(--duration-fast) ease;
}

.photo-remove-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

/* Campaigns section */
.campaigns-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.25rem;
}

.section-title {
  font-family: var(--font-display);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--neutral-800);
  margin: 0;
}

.section-count {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--color-pg-blue);
}

.empty-campaigns {
  padding: 2rem;
  text-align: center;
  color: var(--neutral-500);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.empty-campaigns i {
  font-size: 1.5rem;
}

.empty-campaigns p {
  font-size: var(--text-sm);
  margin: 0;
}

/* Campaign list */
.campaigns-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.campaign-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--surface-card);
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  cursor: pointer;
  transition: all var(--duration-fast) ease;
  -webkit-tap-highlight-color: transparent;
  width: 100%;
  text-align: left;
}

.campaign-item:hover {
  border-color: var(--neutral-300);
  background: var(--neutral-50);
}

.campaign-item:active {
  transform: scale(0.99);
}

.campaign-selected {
  border-color: var(--color-pg-blue);
  background: rgba(0, 61, 165, 0.04);
}

.campaign-checkbox {
  flex-shrink: 0;
}

.campaign-image {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.campaign-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.campaign-brand {
  font-family: var(--font-sans);
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--neutral-500);
}

.campaign-title {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--neutral-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.campaign-reward {
  font-family: var(--font-display);
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--success-600);
  flex-shrink: 0;
}

/* Error message */
.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  color: var(--error-600);
  font-size: var(--text-sm);
}

/* Upload progress */
.upload-progress {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress-text {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--neutral-600);
  text-align: center;
}

/* Footer */
.footer-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.earnings-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: rgba(0, 61, 165, 0.06);
  border-radius: 12px;
}

.earnings-label {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--neutral-600);
}

.earnings-amount {
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-pg-blue);
}

.upload-disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 10px;
  color: #92400e;
  font-size: var(--text-sm);
  line-height: 1.4;
}

.upload-disclaimer i {
  color: #d97706;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.submit-btn {
  width: 100%;
  height: 52px;
  font-size: var(--text-base);
  font-weight: 600;
  border-radius: 12px;
}

.submit-btn:not(:disabled) {
  background: linear-gradient(135deg, var(--color-pg-blue) 0%, var(--color-pg-blue-light) 100%);
}
</style>
